/**
 * Error Boundary Component
 * Catches unhandled React errors and provides fallback UI
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess, Refresh, Home } from '@mui/icons-material';
import { errorHandler } from '../services/errorHandler';
import { ErrorCategory, ErrorSeverity } from '../types/errors';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  errorId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Create structured error and handle it
    const handleErrorAsync = async () => {
      const structuredError = errorHandler.createError(
        ErrorCategory.UI,
        'A component error occurred and the page couldn\'t load properly.',
        {
          severity: ErrorSeverity.HIGH,
          code: 'UI_COMPONENT_CRASH',
          technicalMessage: error.message,
          originalError: error,
          context: {
            feature: 'ui',
            operation: 'component_render',
            parameters: {
              componentStack: errorInfo.componentStack
            }
          },
          retryable: true
        }
      );

      const handledError = await errorHandler.handleError(structuredError);
      this.setState({ errorId: handledError.id });
    };

    handleErrorAsync();

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      errorId: null
    });
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            padding: 3,
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto'
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom color="error">
            Oops! Something went wrong
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            A component error occurred and the page couldn't load properly. 
            This has been automatically reported and will be fixed soon.
          </Typography>

          <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
            <Typography variant="body2">
              Error ID: {this.state.errorId || 'Unknown'}
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={this.handleRetry}
              color="primary"
            >
              Try Again
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={this.handleRefresh}
            >
              Refresh Page
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Home />}
              onClick={this.handleHome}
            >
              Go Home
            </Button>
          </Box>

          {(this.props.showDetails !== false) && (
            <Box sx={{ width: '100%' }}>
              <Button
                onClick={this.toggleDetails}
                startIcon={this.state.showDetails ? <ExpandLess /> : <ExpandMore />}
                size="small"
                sx={{ mb: 2 }}
              >
                {this.state.showDetails ? 'Hide' : 'Show'} Technical Details
              </Button>
              
              <Collapse in={this.state.showDetails}>
                <Alert severity="warning" sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    Error Details
                  </Typography>
                  
                  <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                    <strong>Message:</strong> {this.state.error?.message}
                  </Typography>
                  
                  {this.state.error?.stack && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Stack Trace:</strong>
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          backgroundColor: 'rgba(0,0,0,0.05)',
                          padding: 1,
                          borderRadius: 1,
                          maxHeight: '200px'
                        }}
                      >
                        {this.state.error.stack}
                      </Box>
                    </Box>
                  )}
                  
                  {this.state.errorInfo?.componentStack && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Component Stack:</strong>
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          backgroundColor: 'rgba(0,0,0,0.05)',
                          padding: 1,
                          borderRadius: 1,
                          maxHeight: '200px'
                        }}
                      >
                        {this.state.errorInfo.componentStack}
                      </Box>
                    </Box>
                  )}
                </Alert>
              </Collapse>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
